import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetCompanyByIdQuery } from "../store/apis/companyApi";
import { useGetCampaignByIdQuery } from "../store/apis/campaignApi";
import { useGetUserByIdQuery } from "../store/apis/usersApi";
import { useGetBriefingByIdQuery } from "../store/apis/briefingApi";
import { useGetServiceBriefingQuery } from "../store/apis/serviceBriefingApi";

// One step of the breadcrumb trail. `to` is absent on the last crumb (the
// page you're currently on) and on crumbs that have no page of their own.
export type Crumb = {
  label: string;
  to?: string;
};

// Sections that own a top-level nav entry. The key is the path prefix the
// trail starts from, the value the i18n key for its label — the same keys the
// sidebar uses, so both stay in sync automatically.
const SECTION_LABELS: Record<string, [key: string, fallback: string]> = {
  "/": ["sidebar.home", "Home"],
  "/companies": ["sidebar.company", "Companies"],
  "/campaigns": ["sidebar.campaigns", "Campaigns"],
  "/metrics": ["sidebar.metrics", "Metrics"],
  "/users": ["sidebar.users", "Users"],
  "/purchase-orders": ["sidebar.purchaseOrders", "OCs"],
  "/invoices": ["sidebar.invoices", "Invoices"],
  "/briefings": ["sidebar.briefings", "Briefings"],
  "/commercial-dashboard": [
    "sidebar.commercialDashboard",
    "Dashboard comercial",
  ],
  "/cost-analysis": ["sidebar.costAnalysis", "Análisis de costes"],
  "/control-board": ["sidebar.controlBoard", "Tablero de control"],
  "/client-registry": ["sidebar.clientRegistry", "Client registry"],
  "/commercial-proposals": [
    "sidebar.commercialProposals",
    "Commercial proposals",
  ],
  "/service-briefings": ["sidebar.serviceBriefings", "Service briefings"],
  "/settings": ["sidebar.settings", "Settings"],
  "/system-health": ["sidebar.systemHealth", "System Health"],
  "/credentials": ["sidebar.credentials", "Credentials"],
  "/platform-mappings": ["sidebar.platformMappings", "Platform Mappings"],
  "/data-model": ["sidebar.dataModel", "Data Model"],
  "/processor": ["sidebar.processor", "Processor"],
  "/api": ["sidebar.api", "API"],
  "/wip": ["sidebar.wip", "WIP"],
  "/data-health": ["sidebar.dataHealth", "Data health"],
};

// Routes that live under a section but don't share its URL prefix, so the
// trail can still start from the right parent.
const ALIASED_SECTIONS: { test: RegExp; section: string }[] = [
  { test: /^\/internal-campaigns\//, section: "/campaigns" },
  { test: /^\/campaign\//, section: "/campaigns" },
];

const matchPath = (pathname: string, pattern: RegExp) => pattern.exec(pathname);

// Builds the "where am I" trail for the current URL: section → entity →
// action, e.g. Campaigns → BlackFriday 2025 → Edit. Entity names are looked
// up through the same RTK Query endpoints the pages use, so they come from
// cache (no extra requests) whenever the page already fetched them.
export const useBreadcrumbs = (): Crumb[] => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  // --- Entity ids present in the current path, for the name lookups below ---
  const companyId = (() => {
    const m = matchPath(pathname, /^\/companies\/([^/]+)(?:\/edit)?$/);
    if (!m || m[1] === "new") return undefined;
    return m[1];
  })();

  const campaignId = (() => {
    const own = matchPath(
      pathname,
      /^\/campaigns\/([^/]+)(?:\/edit|\/insertion-orders\/edit-names)?$/
    );
    if (own && own[1] !== "new") return own[1];
    // Internal and merged campaign pages hang off a parent campaign.
    const nested = matchPath(
      pathname,
      /^\/(?:internal-campaigns|campaign)\/([^/]+)\//
    );
    return nested ? nested[1] : undefined;
  })();

  const userId = (() => {
    const m = matchPath(pathname, /^\/users\/([^/]+)(?:\/edit)?$/);
    if (!m || m[1] === "new") return undefined;
    return m[1];
  })();

  const briefingId = (() => {
    const m = matchPath(pathname, /^\/briefings\/([^/]+)(?:\/|$)/);
    if (!m || m[1] === "new") return undefined;
    return m[1];
  })();

  const associatedId = (() => {
    const m = matchPath(
      pathname,
      /^\/briefings\/[^/]+\/associated-campaigns\/([^/]+)(?:\/edit)?$/
    );
    return m ? m[1] : undefined;
  })();

  const { data: companyData } = useGetCompanyByIdQuery(companyId ?? "", {
    skip: !companyId,
  });
  const { data: campaignData } = useGetCampaignByIdQuery(
    { campaignId: campaignId ?? "" },
    { skip: !campaignId }
  );
  const { data: userData } = useGetUserByIdQuery(userId ?? "", {
    skip: !userId,
  });
  const { data: briefingData } = useGetBriefingByIdQuery(
    { id: briefingId ?? "" },
    { skip: !briefingId }
  );

  const serviceBriefingId = (() => {
    const m = matchPath(pathname, /^\/service-briefings\/([^/]+)(?:\/edit)?$/);
    if (!m || m[1] === "new") return undefined;
    return m[1];
  })();
  const { data: serviceBriefingData } = useGetServiceBriefingQuery(
    serviceBriefingId ?? "",
    { skip: !serviceBriefingId }
  );

  const companyName = companyData?.data?.nameCommercial;
  const campaignName = campaignData?.data?.displayName;
  const userName = [userData?.data?.firstName, userData?.data?.lastName]
    .filter(Boolean)
    .join(" ");
  const briefingName = briefingData?.data?.name;
  const serviceBriefingName = serviceBriefingData?.data?.name;

  return useMemo(() => {
    const view = t("sidebar.sub.view", "View");
    const edit = t("sidebar.sub.edit", "Edit");
    const isNew = t("sidebar.sub.new", "New");
    const history = t("sidebar.sub.history", "History");

    // Which top-level section this path belongs to.
    const section =
      ALIASED_SECTIONS.find((alias) => alias.test.test(pathname))?.section ??
      Object.keys(SECTION_LABELS)
        .filter((path) => path !== "/")
        .find((path) => pathname === path || pathname.startsWith(`${path}/`));

    if (!section) {
      // Unknown or root path: only ever a single crumb.
      const label = SECTION_LABELS[pathname];
      return label ? [{ label: t(label[0], label[1]) }] : [];
    }

    const root: Crumb = {
      label: t(SECTION_LABELS[section][0], SECTION_LABELS[section][1]),
      // The last crumb never links; `to` is stripped at the end.
      to: section,
    };
    const rest: Crumb[] = [];

    switch (section) {
      case "/companies": {
        if (pathname === "/companies/new") rest.push({ label: isNew });
        else if (matchPath(pathname, /^\/companies\/([^/]+)\/edit$/)) {
          rest.push({
            label: companyName || view,
            to: `/companies/${companyId}`,
          });
          rest.push({ label: edit });
        } else if (matchPath(pathname, /^\/companies\/[^/]+$/)) {
          rest.push({ label: companyName || view });
        }
        break;
      }
      case "/campaigns": {
        if (pathname === "/campaigns/new") {
          rest.push({ label: isNew });
          break;
        }
        const campaignCrumb: Crumb = {
          label: campaignName || view,
          to: campaignId ? `/campaigns/${campaignId}` : undefined,
        };
        if (/insertion-orders\/edit-names$/.test(pathname)) {
          rest.push(campaignCrumb, {
            label: t("sidebar.sub.editNames", "Edit IO Names"),
          });
        } else if (matchPath(pathname, /^\/campaigns\/[^/]+\/edit$/)) {
          rest.push(campaignCrumb, { label: edit });
        } else if (pathname.startsWith("/internal-campaigns/")) {
          rest.push(campaignCrumb, {
            label: t("sidebar.sub.internalCampaign", "Internal Campaign"),
          });
        } else if (pathname.startsWith("/campaign/")) {
          rest.push(campaignCrumb, {
            label: t("sidebar.sub.mergedCampaign", "Merged Campaign"),
          });
        } else if (matchPath(pathname, /^\/campaigns\/[^/]+$/)) {
          rest.push({ label: campaignName || view });
        }
        break;
      }
      case "/metrics": {
        if (pathname.startsWith("/metrics/internal-metrics/"))
          rest.push({
            label: t("sidebar.sub.internalMargins", "Internal Margins"),
          });
        else if (pathname.startsWith("/metrics/io-margins/"))
          rest.push({ label: t("sidebar.sub.ioMargins", "IO Margins") });
        break;
      }
      case "/users": {
        if (pathname === "/users/new") rest.push({ label: isNew });
        else if (userId && matchPath(pathname, /^\/users\/[^/]+\/edit$/))
          rest.push(
            { label: userName || view, to: `/users/${userId}` },
            { label: edit }
          );
        else if (matchPath(pathname, /^\/users\/[^/]+$/))
          rest.push({ label: userName || view });
        break;
      }
      case "/purchase-orders": {
        if (pathname === "/purchase-orders/history")
          rest.push({ label: history });
        else if (matchPath(pathname, /^\/purchase-orders\/[^/]+\/edit$/))
          rest.push({ label: edit });
        break;
      }
      case "/invoices": {
        if (pathname === "/invoices/history") rest.push({ label: history });
        else if (matchPath(pathname, /^\/invoices\/[^/]+$/))
          rest.push({ label: t("sidebar.sub.details", "Details") });
        break;
      }
      case "/commercial-proposals": {
        if (pathname === "/commercial-proposals/new")
          rest.push({ label: isNew });
        else if (matchPath(pathname, /^\/commercial-proposals\/[^/]+$/))
          rest.push({ label: view });
        break;
      }
      case "/briefings": {
        if (pathname === "/briefings/new") {
          rest.push({ label: isNew });
          break;
        }
        const briefingCrumb: Crumb = {
          label: briefingName || view,
          to: briefingId ? `/briefings/${briefingId}` : undefined,
        };
        if (associatedId) {
          // The associated campaign shows as its concept, not its name.
          const associatedCrumb: Crumb = {
            label: t("sidebar.sub.associatedCampaign", "Associated campaign"),
            to: `/briefings/${briefingId}/associated-campaigns/${associatedId}`,
          };
          if (pathname.endsWith("/edit"))
            rest.push(briefingCrumb, associatedCrumb, { label: edit });
          else rest.push(briefingCrumb, associatedCrumb);
        } else if (matchPath(pathname, /^\/briefings\/[^/]+\/edit$/)) {
          rest.push(briefingCrumb, { label: edit });
        } else if (matchPath(pathname, /^\/briefings\/[^/]+$/)) {
          rest.push({ label: t("sidebar.sub.briefView", "Brief view") });
        }
        break;
      }
      case "/service-briefings": {
        if (pathname === "/service-briefings/new") {
          rest.push({ label: isNew });
        } else if (serviceBriefingId && pathname.endsWith("/edit")) {
          rest.push(
            {
              label: serviceBriefingName || view,
              to: `/service-briefings/${serviceBriefingId}`,
            },
            { label: edit }
          );
        } else if (serviceBriefingId) {
          rest.push({ label: serviceBriefingName || view });
        }
        break;
      }
      default:
        break;
    }

    const crumbs = [root, ...rest];
    // The page you're on is text, not a link.
    return crumbs.map((crumb, index) =>
      index === crumbs.length - 1 ? { label: crumb.label } : crumb
    );
  }, [
    pathname,
    t,
    companyId,
    companyName,
    campaignId,
    campaignName,
    userId,
    userName,
    briefingId,
    briefingName,
    associatedId,
    serviceBriefingId,
    serviceBriefingName,
  ]);
};

export default useBreadcrumbs;
